import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import {
  RecordShipmentResponse,
  RecordShipment,
  CalculateFinalPrice,
  CalculateFinalPriceResponse,
  CarrierTrackingResponseDto,
  JourneyMapResponseDto,
  PaginatedShipmentsResponse,
} from '../../models/record-shipment-response';
import { RecordShipmentRequest } from '../../models/record-shipment-request.model';
import ColisportApiResource from './colisport-api-resource.model';
import {
  CalculateAssuranceFinalPrice,
  CalculateFinalAssurancePriceResponse,
  CalculatePackagesAssurancePrice,
  PackagesAssurancePriceResponse,
  Shipment,
  ValidationStatus,
} from '../../models/shipment.model';
import { GlsRawShop } from '../../utils/glsShopHelpers';
import { FilterShipment } from '../../models/filter-shipment.model';
import { CountryNotSupported } from '../../models/country-not-supported.model';

export default class ShipmentApi implements ColisportApiResource {
  readonly apiUrlPath = 'shipment';

  constructor(readonly httpClient: AxiosInstance) {}

  async downloadPrintingLabels(colisportID: string): Promise<AxiosResponse<ArrayBuffer>> {
    return this.httpClient.get(`${this.apiUrlPath}/printing-labels/${colisportID}`, {
      responseType: 'arraybuffer',
    });
  }

  async record(
    recordShipmentRequest: RecordShipmentRequest,
    isOneWay?: boolean,
  ): Promise<AxiosResponse<RecordShipmentResponse>> {
    return this.httpClient.post(
      `${this.apiUrlPath}/record?isOneWay=${String(isOneWay)}`,
      recordShipmentRequest,
    );
  }

  async validate(
    colisportId: string,
    isOneWay?: boolean,
  ): Promise<AxiosResponse<{ trackingNumber: string; validationStatus: ValidationStatus }>> {
    return this.httpClient.post(
      `${this.apiUrlPath}/validate/${colisportId}?isOneWay=${String(isOneWay)}`,
    );
  }

  getShipmentsByEmail(email: string, isOneWay?: boolean): Promise<AxiosResponse<RecordShipment[]>> {
    return this.httpClient.get(`${this.apiUrlPath}/orders/${email}?isOneWay=${String(isOneWay)}`);
  }

  /** P2 (doc 17 §B): nota de erro do widget de pagamento (diário append-only
   *  do shipment; o admin vê no detalhe). Fire-and-forget no caller. */
  reportPaymentWidgetError(
    colisportID: string,
    payload: unknown,
    isOneWay?: boolean,
  ): Promise<AxiosResponse<Record<string, never>>> {
    return this.httpClient.post(`${this.apiUrlPath}/payment-widget-error/${colisportID}`, {
      isOneWay,
      payload,
    });
  }

  // C1.5 (S-01): config axios OPCIONAL (aditivo) - a página accept-payment
  // anexa o shipment-token do ?t= via header X-Shipment-Token.
  getShipmentByColisportID(
    id: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<Shipment>> {
    return this.httpClient.get(`${this.apiUrlPath}/${id}`, config);
  }

  deleteShipmentByColisportID(
    colisportID: string,
  ): Promise<AxiosResponse<{ deletedCount: number }>> {
    return this.httpClient.delete(`${this.apiUrlPath}/delete/${colisportID}`);
  }

  update(
    colisportID: string,
    data: Partial<Shipment>,
    isOneWay?: boolean,
  ): Promise<AxiosResponse<{ deletedCount: number }>> {
    return this.httpClient.put(
      `${this.apiUrlPath}/update/${colisportID}?isOneWay=${String(isOneWay)}`,
      data,
    );
  }

  async getShipmentsBackOffice(query?: string): Promise<AxiosResponse<FilterShipment>> {
    return this.httpClient.get(`${this.apiUrlPath}/filters${query !== '' ? `?${query}` : ''}`, {
      params: `filters?${query}`,
    });
  }

  async revolutPaidCheck(id: string): Promise<AxiosResponse<boolean>> {
    return this.httpClient.get(`${this.apiUrlPath}/isPaidViaRevolut/${id}`);
  }

  async billingInfoCheck(id: string): Promise<AxiosResponse<boolean>> {
    return this.httpClient.get(`${this.apiUrlPath}/hasBillingInfo/${id}`);
  }

  async getVatDetails(
    startDate: Date,
    endDate: Date,
    timezone: string,
  ): Promise<AxiosResponse<Blob>> {
    return this.httpClient.post(
      `${this.apiUrlPath}/vatDetails`,
      { startDate, endDate, timezone },
      {
        responseType: 'blob',
      },
    );
  }

  async getFinalPrice(
    calculateFinalPrice: CalculateFinalPrice,
  ): Promise<AxiosResponse<CalculateFinalPriceResponse>> {
    return this.httpClient.post(`${this.apiUrlPath}/finalPrice`, {
      isIndividualAccountType: calculateFinalPrice.isIndividualAccountType,
      assuranceAnnulationValue: calculateFinalPrice.assuranceAnnulationValue,
      packagesAssuranceValues: calculateFinalPrice.packagesAssuranceValues,
      offerPriceWithVAT: calculateFinalPrice.offerPriceWithVAT,
      originCountry: calculateFinalPrice.originCountry,
      destinationCountry: calculateFinalPrice.destinationCountry,
    });
  }

  async getPackagesAssurancePrice(
    calculatePackagesAssurancePrice: CalculatePackagesAssurancePrice,
    // Config axios OPCIONAL (aditivo, retrocompatível: callers antigos passam 1
    // arg). Usado pelo palco para encaminhar um AbortSignal (`{ signal }`) e
    // cancelar de verdade chamadas obsoletas / em timeout. Ver useDebouncedResource.
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<PackagesAssurancePriceResponse>> {
    return this.httpClient.post(
      `${this.apiUrlPath}/packagesAssurancePrice`,
      {
        isIndividualAccountType: calculatePackagesAssurancePrice.isIndividualAccountType,
        packagesAssuranceValues: calculatePackagesAssurancePrice.packagesAssuranceValues,
        ...(calculatePackagesAssurancePrice.userEmail
          ? { userEmail: calculatePackagesAssurancePrice.userEmail }
          : {}),
      },
      config,
    );
  }

  async getAssuranceFinalPrices(
    calculateAssuranceFinalPrice: CalculateAssuranceFinalPrice,
    // Config axios OPCIONAL (aditivo, retrocompatível). O palco passa um
    // AbortSignal via useDebouncedResource para cancelar chamadas obsoletas /
    // em timeout (mesmo padrão de getPackagesAssurancePrice).
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<CalculateFinalAssurancePriceResponse>> {
    return this.httpClient.post(
      `${this.apiUrlPath}/finalAssurancePrices`,
      {
        isIndividualAccountType: calculateAssuranceFinalPrice.isIndividualAccountType,
        hasAssuranceAnnulation: calculateAssuranceFinalPrice.hasAssuranceAnnulation,
        packagesAssuranceValues: calculateAssuranceFinalPrice.packagesAssuranceValues,
        originCountry: calculateAssuranceFinalPrice.originCountry,
        userEmail: calculateAssuranceFinalPrice?.userEmail,
      },
      config,
    );
  }

  async calculateInternational(data: CountryNotSupported): Promise<AxiosResponse> {
    return this.httpClient.post(`${this.apiUrlPath}/calculateInternational`, data);
  }

  // C1.6 (S-01): mesmo config opcional do getShipmentByColisportID.
  acceptExtraPayment(
    colisportID: string,
    priceID: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse> {
    return this.httpClient.put(
      `${this.apiUrlPath}/extra-payment/accept/${colisportID}/${priceID}`,
      undefined,
      config,
    );
  }

  getShipmentsByEmailPaginated(
    email: string,
    page: number,
    limit: number,
    isOneWay?: boolean,
    status?: string,
    search?: string,
  ): Promise<AxiosResponse<PaginatedShipmentsResponse>> {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
      isOneWay: String(isOneWay ?? false),
    });
    if (status) params.set('status', status);
    if (search?.trim()) params.set('search', search.trim());
    return this.httpClient.get(`${this.apiUrlPath}/orders-paginated/${email}?${params.toString()}`);
  }

  getCarrierTracking(colisportID: string): Promise<AxiosResponse<CarrierTrackingResponseDto>> {
    return this.httpClient.get(`${this.apiUrlPath}/carrier-tracking/${colisportID}`);
  }

  getJourneyMap(colisportID: string): Promise<AxiosResponse<JourneyMapResponseDto>> {
    return this.httpClient.get(`${this.apiUrlPath}/journey-map/${colisportID}`);
  }

  async downloadProofOfDelivery(colisportID: string): Promise<AxiosResponse<{ url: string }>> {
    return this.httpClient.get(`${this.apiUrlPath}/proof-of-delivery/${colisportID}`);
  }

  async searchGlsShops(
    postalCode: string,
    city: string,
    countryCode: string,
  ): Promise<AxiosResponse<GlsRawShop[]>> {
    return this.httpClient.post(`${this.apiUrlPath}/gls-shops/search`, {
      postalCode,
      city,
      countryCode,
    });
  }
}
